import React, { useState, useEffect } from "react";
import { Link, useLocation, useNavigate } from "react-router-dom";
import { useAuth } from "../lib/auth";
import { api } from "../lib/api";
import { Search, ShoppingCart, User, Menu, X, ChevronDown, Layers, LogOut, LayoutDashboard, Shield, MessageSquare } from "lucide-react";

const expertCategories = [
  { label: "3D Designers", items: ["CAD Designer", "Product Designer", "3D Artist", "Mechanical Designer", "Architectural Designer"] },
  { label: "3D Printer Owners", items: ["FDM", "Resin", "SLA", "Industrial", "Bulk Production"] },
  { label: "Others", items: ["Engineers", "Consultants", "Reverse Engineering", "Product Development"] },
];

export default function Header() {
  const { user, logout } = useAuth();
  const [open, setOpen] = useState(false);
  const [expertsOpen, setExpertsOpen] = useState(false);
  const [search, setSearch] = useState("");
  const [unread, setUnread] = useState(0);
  const nav = useNavigate();
  const loc = useLocation();
  const isAuthed = user && user !== false;

  useEffect(() => {
    if (!isAuthed) return;
    const poll = () => api.get("/conversations/unread/count").then(r => setUnread(r.data.unread || 0)).catch(() => {});
    poll();
    const t = setInterval(poll, 20000);
    return () => clearInterval(t);
  }, [isAuthed, loc.pathname]);

  const submitSearch = (e) => {
    e.preventDefault();
    if (search.trim()) nav(`/experts?q=${encodeURIComponent(search)}`);
  };

  return (
    <header data-testid="global-header" className="sticky top-0 z-50 backdrop-blur-xl bg-[#FFFFFF]/85 border-b border-slate-200">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="flex items-center h-[68px] gap-4 lg:gap-8">
          <Link to="/" data-testid="logo-link" className="flex items-center gap-2.5 shrink-0 group">
            <div className="w-9 h-9 rounded-xl bg-gradient-to-br from-[#2563EB] to-[#0891B2] flex items-center justify-center shadow-[0_4px_16px_rgba(37,99,235,0.4)] group-hover:shadow-[0_6px_22px_rgba(6,182,212,0.45)] transition-shadow">
              <Layers className="w-4 h-4 text-white" strokeWidth={2.5} />
            </div>
            <span className="font-display font-bold text-lg tracking-tight">Trinity Layers</span>
          </Link>

          <nav className="hidden lg:flex items-center gap-1 ml-4">
            <Link data-testid="nav-home" to="/" className="px-3 py-2 text-sm font-medium text-[#475569] hover:text-slate-900 rounded-lg hover:bg-slate-50 transition-colors">Home</Link>
            <div className="relative" onMouseEnter={() => setExpertsOpen(true)} onMouseLeave={() => setExpertsOpen(false)}>
              <button data-testid="nav-experts-btn" className="px-3 py-2 text-sm font-medium text-[#475569] hover:text-slate-900 rounded-lg hover:bg-slate-50 transition-colors flex items-center gap-1">
                Experts <ChevronDown className="w-3 h-3" />
              </button>
              {expertsOpen && (
                <div data-testid="experts-mega-menu" className="absolute left-0 top-full pt-3 w-[640px]">
                  <div className="glass rounded-2xl p-6 grid grid-cols-3 gap-6 tl-fade-up">
                    {expertCategories.map((cat) => (
                      <div key={cat.label}>
                        <div className="tl-label mb-3">{cat.label}</div>
                        <div className="flex flex-col gap-1.5">
                          {cat.items.map((it) => (
                            <Link key={it} to={`/experts?category=${encodeURIComponent(it)}`}
                              className="text-sm text-[#475569] hover:text-[#0891B2] transition-colors">{it}</Link>
                          ))}
                        </div>
                      </div>
                    ))}
                    <div className="col-span-3 pt-4 border-t border-slate-200 flex gap-3">
                      <Link to="/join/expert" data-testid="cta-join-expert" className="tl-btn-ghost text-sm">Join as Expert</Link>
                      <Link to="/join/partner" data-testid="cta-join-partner" className="tl-btn-ghost text-sm">Join as 3D Printer Owner</Link>
                    </div>
                  </div>
                </div>
              )}
            </div>
            <Link data-testid="nav-shop" to="/shop" className="px-3 py-2 text-sm font-medium text-[#475569] hover:text-slate-900 rounded-lg hover:bg-slate-50 transition-colors">Shop</Link>
            <Link data-testid="nav-resources" to="/resources" className="px-3 py-2 text-sm font-medium text-[#475569] hover:text-slate-900 rounded-lg hover:bg-slate-50 transition-colors">Resources</Link>
          </nav>

          <form onSubmit={submitSearch} className="hidden md:flex flex-1 max-w-sm relative">
            <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-[#475569]" />
            <input data-testid="header-search" value={search} onChange={(e) => setSearch(e.target.value)}
              placeholder="Search experts, products…" className="tl-input pl-9 py-2.5 text-sm rounded-full" />
          </form>

          <div className="flex items-center gap-2 ml-auto">
            {isAuthed && (
              <Link to="/messages" data-testid="nav-messages" className="p-2 rounded-lg hover:bg-slate-100 transition-colors relative">
                <MessageSquare className="w-5 h-5 text-[#475569]" />
                {unread > 0 && (
                  <span data-testid="unread-count" className="absolute -top-0.5 -right-0.5 min-w-[18px] h-[18px] px-1 rounded-full bg-[#0891B2] text-[10px] text-white font-bold flex items-center justify-center">
                    {unread > 9 ? "9+" : unread}
                  </span>
                )}
              </Link>
            )}
            <Link to="/cart" data-testid="nav-cart" className="p-2 rounded-lg hover:bg-slate-100 transition-colors relative">
              <ShoppingCart className="w-5 h-5 text-[#475569]" />
            </Link>
            {isAuthed ? (
              <div className="relative group">
                <button data-testid="user-menu-btn" className="flex items-center gap-2 px-3 py-1.5 rounded-lg hover:bg-slate-100 transition-colors">
                  <div className="w-7 h-7 rounded-full bg-gradient-to-br from-[#2563EB] to-[#0891B2] flex items-center justify-center text-xs font-semibold text-white">
                    {(user.name || user.email)[0].toUpperCase()}
                  </div>
                  <span className="hidden md:inline text-sm">{user.name?.split(" ")[0]}</span>
                </button>
                <div className="absolute right-0 top-full pt-2 opacity-0 invisible group-hover:opacity-100 group-hover:visible transition-all w-56">
                  <div className="glass rounded-xl p-2">
                    <div className="px-3 py-2 border-b border-slate-200 mb-1">
                      <div className="text-xs text-[#475569]">{user.email}</div>
                      <div className="text-xs tl-label mt-0.5">{user.role}</div>
                    </div>
                    <Link to="/profile" data-testid="menu-profile" className="flex items-center gap-2 px-3 py-2 text-sm rounded hover:bg-slate-100"><User className="w-4 h-4" /> Profile</Link>
                    <Link to="/messages" data-testid="menu-messages" className="flex items-center gap-2 px-3 py-2 text-sm rounded hover:bg-slate-100"><MessageSquare className="w-4 h-4" /> Messages</Link>
                    {["EXPERT","PRINT_PARTNER"].includes(user.role) && (
                      <Link to="/dashboard" data-testid="menu-dashboard" className="flex items-center gap-2 px-3 py-2 text-sm rounded hover:bg-slate-100"><LayoutDashboard className="w-4 h-4" /> Dashboard</Link>
                    )}
                    {["ADMIN","SUPER_ADMIN"].includes(user.role) && (
                      <Link to="/admin" data-testid="menu-admin" className="flex items-center gap-2 px-3 py-2 text-sm rounded hover:bg-slate-100"><Shield className="w-4 h-4" /> Admin</Link>
                    )}
                    <button data-testid="menu-logout" onClick={logout} className="w-full text-left flex items-center gap-2 px-3 py-2 text-sm rounded hover:bg-slate-100"><LogOut className="w-4 h-4" /> Logout</button>
                  </div>
                </div>
              </div>
            ) : (
              <>
                <Link to="/login" data-testid="nav-login" className="hidden sm:inline text-sm px-3 py-2 text-[#475569] hover:text-slate-900">Login</Link>
                <Link to="/register" data-testid="nav-register" className="tl-btn-primary text-sm">Get Started</Link>
              </>
            )}
            <button data-testid="mobile-menu-btn" onClick={() => setOpen(!open)} className="lg:hidden p-2">
              {open ? <X className="w-5 h-5" /> : <Menu className="w-5 h-5" />}
            </button>
          </div>
        </div>

        {open && (
          <div data-testid="mobile-menu" className="lg:hidden py-4 border-t border-slate-200">
            <div className="flex flex-col gap-2">
              <Link to="/" onClick={() => setOpen(false)} className="py-2 text-sm">Home</Link>
              <Link to="/experts" onClick={() => setOpen(false)} className="py-2 text-sm">Experts</Link>
              <Link to="/experts/printing-partners" onClick={() => setOpen(false)} className="py-2 text-sm">3D Printer Owners</Link>
              <Link to="/shop" onClick={() => setOpen(false)} className="py-2 text-sm">Shop</Link>
              <Link to="/resources" onClick={() => setOpen(false)} className="py-2 text-sm">Resources</Link>
              <Link to="/join/expert" onClick={() => setOpen(false)} className="py-2 text-sm text-[#0891B2]">Join as Expert</Link>
            </div>
          </div>
        )}
      </div>
    </header>
  );
}
